import { Turbo } from "@hotwired/turbo-rails";
import { CustomizationPayloadEvent } from "./event";
import Customization from "./index";

class CustomizeRequest {
  dispatchPreview(data) {
    document
      .getElementById("site-builder-preview")
      .contentWindow.postMessage(new CustomizationPayloadEvent(data).toObj);
  }

  notifyUpdateFinished(updates = {}) {
    /*
        Return if not loaded in an iframe
     */
    if (window.self === window.top) return;

    const data = { rendered: true, ...updates };
    parent.postMessage(new CustomizationPayloadEvent(data).toObj);
  }

  notifyPageChanged() {
    /*
        Return if not loaded in an iframe
     */
    if (window.self === window.top) return;

    parent.postMessage({ type: "siteBuilderPageChange" });
  }

  previewIframe() {
    return window.frames["site-builder-preview"]?.contentDocument;
  }

  updateCssVariables(data) {
    for (const [id, value] of Object.entries(data)) {
      this.#updateCssVariable(id, value);
    }
  }

  getLayoutId() {
    const element = this.previewIframe().querySelector(
      "[data-site-builder-layout]"
    );

    // Not available in non persistence mode
    if (!element) return;

    return element.dataset.siteBuilderLayout;
  }

  getFontPairing() {
    const element = this.previewIframe().querySelector(
      "[data-site-builder-font-pairing]"
    );

    // Not available in non persistence mode
    if (!element) return;

    return element.dataset.siteBuilderFontPairing;
  }

  getSiteOwnerBarPayload() {
    const payload = this.previewIframe().querySelector(
      "[data-site-owner-bar-payload]"
    );
    if (payload) {
      return JSON.parse(payload.dataset.siteOwnerBarPayload);
    }
  }

  getPageUrl() {
    const element = this.previewIframe().querySelector("[data-page-url]");
    if (element) {
      return element.dataset.pageUrl;
    }
  }

  getCurrentImageSet() {
    const element = this.previewIframe().querySelector("[data-site-builder-image-set]");
    if (element) {
      return element.dataset.siteBuilderImageSet;
    }
  }

  heroImage(){
    return this.previewIframe().querySelector("[data-site-builder-hero-image-url]")?.dataset?.siteBuilderHeroImageUrl;
  }

  footerOneImage(){
    return this.previewIframe().querySelector("[data-site-builder-footer-one-image-url]")?.dataset?.siteBuilderFooterOneImageUrl;
  }

  footerTwoImage(){
    return this.previewIframe().querySelector("[data-site-builder-footer-two-image-url]")?.dataset?.siteBuilderFooterTwoImageUrl;
  }

  getCssVariable(key) {
    const root = this.previewIframe().querySelector(":root");
    const store = getComputedStyle(root);
    return store.getPropertyValue(`--${key}`);
  }

  async updateLiquidTags(data) {
    const isPersisted = !!document.body.dataset.siteBuilderSite;
    if (!isPersisted) {
      delete data["logo"];
    }
    const values = this.#parseImages(data);
    const url = this.destination(values);
    try {
      await this.turboFetch(url);
    } catch (_) {
      await this.#handleRequestError(url);
    }
  }

  #updateCssVariable(key, value, shallPrepend = false) {
    const root = document.querySelector(":root");
    const store = getComputedStyle(root);
    root.style.setProperty(
      `--${key}`,
      shallPrepend ? `${value}, ${store.getPropertyValue(`--${key}`)}` : value
    );
  }

  #parseImages(data) {
    const result = { ...data };
    ['hero', 'footer-one', 'footer-two'].forEach((image) => {
      if (result[image]?.url) {
        result[image] = result[image].url;
      }
    })
    return result;
  }

  async turboFetch(url, fetchProps) {
    const response = await fetch(url, {
      ...fetchProps,
      headers: {
        "Content-Type": "application/json",
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.head.querySelector("[name='csrf-token']")
          .content,
      },
    });
    const turboFrame = await response.text();

    if (response.ok) {
      Turbo.renderStreamMessage(turboFrame);
      return response;
    } else {
      throw new Error(turboFrame);
    }
  }

  destination(values) {
    const { url } = values;
    const existingParams = location.search;
    const pathPrefix = url ?? "/";
    return `${pathPrefix}${existingParams}&${new URLSearchParams(values)}`;
  }

  async #handleRequestError(url) {
    console.error(`Turbo Stream request failed for ${url}`);
    if (!url.match(/^\/\?/)) {
      await Customization.pageLoad("/");
    }
  }
}

export default new CustomizeRequest();
